import { useState } from 'react';
import SiteNav from './SiteNav';
import BlogFooter from './BlogFooter';
import SEO from './SEO';
import { SOS_DETRESSE_LUXEMBOURG } from '../content/urgentSupport';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Resource {
  name: string;
  number: string;
  description?: string;
  availability?: string;
  site?: string;
  siteUrl?: string;
}

interface CountrySection {
  id: string;
  flag: string;
  label: string;
  urgencies: { service: string; number: string }[];
  support: Resource[];
  violence: Resource[];
  violenceNote?: string;
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const COUNTRIES: CountrySection[] = [
  {
    id: 'belgique',
    flag: '🇧🇪',
    label: 'Belgique',
    urgencies: [
      { service: 'Police Secours', number: '101' },
      { service: 'Aide Médicale Urgente / Pompiers', number: '112' },
      { service: 'Service de Garde Médicale', number: '1733' },
    ],
    support: [
      {
        name: 'Télé-Accueil',
        number: '107',
        description: 'Écoute pour toute personne en situation de crise, de solitude ou de difficulté morale. Gratuit et anonyme.',
        availability: '24h/24, 7j/7',
        site: 'tele-accueil.be',
        siteUrl: 'https://www.tele-accueil.be',
      },
      {
        name: 'Centre de Prévention du Suicide',
        number: '+32 800 32 123',
        description: 'Soutien psychologique pour les personnes en crise suicidaire et leur entourage. Gratuit.',
        availability: '24h/24, 7j/7',
        site: 'preventionsuicide.be',
        siteUrl: 'https://www.preventionsuicide.be',
      },
      {
        name: 'Écoute-Enfants',
        number: '103',
        description: "Ligne d'écoute pour les enfants et adolescents. Gratuit et anonyme.",
        availability: '10h–minuit, 7j/7',
        site: '103ecoute.be',
        siteUrl: 'https://www.103ecoute.be',
      },
      {
        name: 'Urgence Sociale Wallonie',
        number: '1718',
        description: "Aides d'urgence sociale (logement, alimentation, etc.).",
        availability: 'Horaires de bureau',
        site: 'wallonie.be',
        siteUrl: 'https://www.wallonie.be',
      },
    ],
    violence: [
      {
        name: 'Écoute Violences Conjugales',
        number: '+32 800 30 030',
        description: 'Soutien, information et orientation pour les victimes de violences intrafamiliales et conjugales. Gratuit.',
        availability: 'Horaires variables',
        site: 'ecouteviolencesconjugales.be',
        siteUrl: 'https://www.ecouteviolencesconjugales.be',
      },
      {
        name: 'SOS Viol',
        number: '+32 800 98 100',
        description: 'Accompagnement psychologique, médical et juridique pour les victimes de violences sexuelles. Gratuit.',
        availability: 'Lun–Ven 8h–18h',
        site: 'sosviol.be',
        siteUrl: 'https://www.sosviol.be',
      },
      {
        name: 'CPVS',
        number: 'Bruxelles : +32 2 535 45 42 · Liège : +32 4 323 93 11 · Namur : +32 81 72 62 62',
        description: 'Soins médicaux, psychologiques et aide juridique en un seul lieu.',
        availability: '24h/24, 7j/7',
        site: 'cpvs.belgium.be',
        siteUrl: 'https://cpvs.belgium.be',
      },
      {
        name: 'Ligne 1712',
        number: '1712',
        description: 'Ligne flamande et bruxelloise pour les abus et la maltraitance.',
        availability: 'Horaires variables',
        site: '1712.be',
        siteUrl: 'https://www.1712.be',
      },
      {
        name: 'Child Focus',
        number: '116 000',
        description: "Aide aux enfants disparus et lutte contre l'exploitation sexuelle.",
        availability: '24h/24, 7j/7',
        site: 'childfocus.be',
        siteUrl: 'https://www.childfocus.be',
      },
    ],
  },
  {
    id: 'france',
    flag: '🇫🇷',
    label: 'France',
    urgencies: [
      { service: 'SAMU (Urgences Médicales)', number: '15' },
      { service: 'Police Secours', number: '17' },
      { service: 'Pompiers', number: '18' },
      { service: 'Urgence par SMS (sourds/malentendants)', number: '114' },
    ],
    support: [
      {
        name: 'Prévention du Suicide',
        number: '3114',
        description: 'Numéro national gratuit pour les personnes en détresse psychologique. Professionnels formés.',
        availability: '24h/24, 7j/7',
        site: '3114.fr',
        siteUrl: 'https://www.3114.fr',
      },
      {
        name: 'SOS Amitié',
        number: '+33 9 72 39 40 50',
        description: 'Soutien émotionnel pour toute personne en détresse ou isolement. Bénévoles formés.',
        availability: '24h/24, 7j/7',
        site: 'sosamitie.org',
        siteUrl: 'https://www.sosamitie.org',
      },
      {
        name: 'Croix-Rouge Écoute',
        number: '+33 800 858 858',
        description: 'Soutien psychosocial par téléphone.',
        availability: 'Lun–Ven 9h–19h, Sam–Dim 12h–18h',
        site: 'croix-rouge.fr',
        siteUrl: 'https://www.croix-rouge.fr',
      },
      {
        name: 'Fil Santé Jeunes',
        number: '+33 800 235 236',
        description: 'Soutien psychologique pour les 12–25 ans.',
        availability: '7j/7 9h–23h',
        site: 'filsantejeunes.com',
        siteUrl: 'https://www.filsantejeunes.com',
      },
    ],
    violence: [
      {
        name: 'Violences Femmes Info',
        number: '3919',
        description: 'Numéro national de référence pour les femmes victimes de violences.',
        availability: '24h/24, 7j/7',
        site: 'solidaritefemmes.org',
        siteUrl: 'https://www.solidaritefemmes.org',
      },
      {
        name: 'Viols Femmes Informations',
        number: '+33 800 05 95 95',
        description: "Ligne gratuite et anonyme pour les victimes de viol et d'agressions sexuelles.",
        availability: 'Lun–Ven 10h–19h',
        site: 'cfcv.asso.fr',
        siteUrl: 'https://www.cfcv.asso.fr',
      },
      {
        name: 'France Victimes',
        number: '116 006',
        description: "Aide à toutes les victimes d'infractions.",
        availability: '7j/7 9h–19h',
        site: 'france-victimes.fr',
        siteUrl: 'https://www.france-victimes.fr',
      },
      {
        name: 'Enfance en Danger',
        number: '119',
        description: 'Pour les mineurs victimes de violences.',
        availability: '24h/24, 7j/7',
        site: 'allo119.gouv.fr',
        siteUrl: 'https://www.allo119.gouv.fr',
      },
      {
        name: 'SOS Homophobie',
        number: '+33 1 48 06 42 41',
        description: 'Soutien pour les victimes de violences anti-LGBTQIA+.',
        availability: 'Jeu 20h–22h, Dim 18h–20h',
        site: 'sos-homophobie.org',
        siteUrl: 'https://www.sos-homophobie.org',
      },
    ],
  },
  {
    id: 'luxembourg',
    flag: '🇱🇺',
    label: 'Luxembourg',
    urgencies: [
      { service: 'Police Grand-Ducale', number: '113' },
      { service: 'Ambulance / Pompiers / Protection Civile', number: '112' },
    ],
    support: [
      {
        name: SOS_DETRESSE_LUXEMBOURG.name,
        number: SOS_DETRESSE_LUXEMBOURG.internationalNumber,
        description: 'Accompagnement gratuit, confidentiel et anonyme pour toute personne traversant une période difficile.',
        availability: SOS_DETRESSE_LUXEMBOURG.availability,
        site: '454545.lu',
        siteUrl: 'https://www.454545.lu',
      },
      {
        name: 'Helpline Croix-Rouge',
        number: '+352 2755',
        description: 'Aide sociale et écoute.',
        availability: 'Lun–Ven 8h–17h',
        site: 'croix-rouge.lu',
        siteUrl: 'https://www.croix-rouge.lu',
      },
      {
        name: 'Kanner-Jugendtelefon (KJT)',
        number: '116 111',
        description: "Ligne d'écoute gratuite et anonyme pour les enfants et les jeunes.",
        availability: '8h–22h',
        site: 'kjt.lu',
        siteUrl: 'https://www.kjt.lu',
      },
    ],
    violence: [
      {
        name: 'Centre National pour Victimes (CNVV)',
        number: '+352 2060 1060',
        description: 'Helpline nationale pour les victimes de violences physiques, sexuelles ou psychiques.',
        availability: '24h/24, 7j/7',
        site: 'croix-rouge.lu/cnvv',
        siteUrl: 'https://www.croix-rouge.lu/cnvv',
      },
      {
        name: 'Femmes en Détresse',
        number: '+352 44 81 81',
        description: 'Aide et protection pour les femmes victimes de violences domestiques.',
        availability: 'Horaires variables',
        site: 'fed.lu',
        siteUrl: 'https://www.fed.lu',
      },
      {
        name: 'Planning Familial – Info Viol',
        number: '+352 49 58 54',
        description: "Soutien et information pour les victimes d'agressions sexuelles.",
        availability: 'Horaires variables',
        site: 'planning-familial.lu',
        siteUrl: 'https://www.planning-familial.lu',
      },
      {
        name: 'UMEDO',
        number: '+352 621 85 80 80',
        description: "Documentation médicale des blessures pour les victimes de violences ne souhaitant pas porter plainte immédiatement.",
        availability: '24h/24, 7j/7',
        site: 'umedo.lu',
        siteUrl: 'https://www.umedo.lu',
      },
    ],
  },
  {
    id: 'suisse',
    flag: '🇨🇭',
    label: 'Suisse',
    urgencies: [
      { service: 'Police', number: '117' },
      { service: 'Ambulance / Urgences Sanitaires', number: '144' },
      { service: 'Pompiers', number: '118' },
    ],
    support: [
      {
        name: 'La Main Tendue',
        number: '143',
        description: "Espace d'écoute bienveillante pour les soucis du quotidien, la solitude ou les situations de crise.",
        availability: '24h/24, 7j/7',
        site: '143.ch',
        siteUrl: 'https://www.143.ch',
      },
      {
        name: 'Pro Juventute',
        number: '147',
        description: "Ligne d'aide professionnelle pour les enfants et adolescents.",
        availability: '24h/24, 7j/7',
        site: '147.ch',
        siteUrl: 'https://www.147.ch',
      },
      {
        name: 'LGBTIQ Helpline',
        number: '+41 800 133 133',
        description: 'Soutien pour les personnes de la communauté LGBTQIA+.',
        availability: 'Mar et Ven 19h–21h',
        site: 'lgbtiq-helpline.ch',
        siteUrl: 'https://www.lgbtiq-helpline.ch',
      },
    ],
    violenceNote: "Depuis le 1er mai 2026, la Suisse dispose d'un numéro national unifié pour les victimes de violence : le 142 (gratuit, 24h/24, 7j/7). Ce numéro est dédié au conseil et à l'orientation en situation de crise — il ne remplace pas les services d'urgence (117 / 144 / 118).",
    violence: [
      {
        name: 'Aide aux Victimes – LAVI',
        number: '142',
        description: "Numéro national unique pour les victimes de violence physique, psychique ou sexuelle. Conseil et orientation. Pas un numéro d'urgence.",
        availability: '24h/24, 7j/7',
        site: 'aide-aux-victimes.ch',
        siteUrl: 'https://www.aide-aux-victimes.ch',
      },
      {
        name: 'Agression Sexuelle Urgences',
        number: 'Voir site',
        description: "Plateforme orientant vers les urgences des hôpitaux romands (CHUV, HUG, etc.).",
        availability: '24h/24, 7j/7',
        site: 'agression-sexuelle-urgences.ch',
        siteUrl: 'https://www.agression-sexuelle-urgences.ch',
      },
      {
        name: 'Viol-Secours (Genève)',
        number: '+41 22 345 20 20',
        description: 'Soutien psychologique et social pour les victimes de violences sexuelles.',
        availability: 'Horaires variables',
        site: 'viol-secours.ch',
        siteUrl: 'https://www.viol-secours.ch',
      },
      {
        name: 'Centre d\'accueil MalleyPrairie (Vaud)',
        number: '+41 21 620 76 76',
        description: "Accueil et hébergement d'urgence pour les victimes de violences conjugales.",
        availability: '24h/24, 7j/7',
        site: 'malleyprairie.ch',
        siteUrl: 'https://www.malleyprairie.ch',
      },
    ],
  },
];

const PROFESSIONAL_RESOURCES = [
  { country: '🇧🇪', label: 'Commission des Psychologues (ComPsy)', url: 'https://www.compsy.be/search', display: 'compsy.be/search' },
  { country: '🇧🇪', label: 'UPPCF', url: 'https://www.uppcf.be', display: 'uppcf.be' },
  { country: '🇧🇪', label: 'Rosa.be', url: 'https://www.rosa.be', display: 'rosa.be' },
  { country: '🇫🇷', label: 'Mon Soutien Psy (Assurance Maladie)', url: 'https://www.sante.fr', display: 'sante.fr' },
  { country: '🇫🇷', label: 'Annuaire Santé Ameli', url: 'https://www.annuairesante.ameli.fr', display: 'annuairesante.ameli.fr' },
  { country: '🇫🇷', label: 'Psycom', url: 'https://www.psycom.org', display: 'psycom.org' },
  { country: '🇱🇺', label: 'SLP – Psyregister', url: 'https://www.slp.lu/fr/psyregister/', display: 'slp.lu/fr/psyregister' },
  { country: '🇨🇭', label: 'FSP Psyfinder', url: 'https://www.psychologie.ch/fr/psyfinder', display: 'psychologie.ch/fr/psyfinder' },
  { country: '🇨🇭', label: 'PsyReg (OFSP)', url: 'https://www.bag.admin.ch', display: 'bag.admin.ch' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function PhoneLink({ number }: { number: string }) {
  // Handle multi-number strings (CPVS)
  if (number.includes('·') || number.includes('/') || number === 'Voir site') {
    return <span className="font-sans font-bold text-[#1B2A3A]">{number}</span>;
  }
  const digits = number.replace(/\s/g, '');
  return (
    <a
      href={`tel:${digits}`}
      className="font-sans font-bold text-[#1B2A3A] hover:text-[#6B9FD4] transition-colors"
    >
      {number}
    </a>
  );
}

function ResourceCard({ resource }: { resource: Resource }) {
  return (
    <div className="bg-white border border-[#CBBBA8]/30 rounded-xl p-5 flex flex-col gap-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <span className="font-sans font-semibold text-[#1B2A3A] text-[14px] leading-snug">{resource.name}</span>
        {resource.availability && (
          <span className="text-[11px] text-[#1B2A3A]/50 font-sans whitespace-nowrap">{resource.availability}</span>
        )}
      </div>
      <PhoneLink number={resource.number} />
      {resource.description && (
        <p className="text-[13px] text-[#1B2A3A]/70 font-sans leading-relaxed">{resource.description}</p>
      )}
      {resource.site && resource.siteUrl && (
        <a
          href={resource.siteUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[12px] text-[#2B5F8E] hover:underline font-sans"
        >
          {resource.site}
        </a>
      )}
    </div>
  );
}

function UrgencyTable({ items }: { items: { service: string; number: string }[] }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-[#CBBBA8]/30">
      <table className="w-full min-w-[320px]" role="table" aria-label="Numéros d'urgence">
        <thead>
          <tr className="bg-[#F9F5F0]">
            <th className="text-left px-4 py-3 text-[11px] uppercase tracking-widest text-[#1B2A3A]/50 font-sans font-semibold">Service</th>
            <th className="text-left px-4 py-3 text-[11px] uppercase tracking-widest text-[#1B2A3A]/50 font-sans font-semibold">Numéro</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={i} className="border-t border-[#CBBBA8]/20 bg-white">
              <td className="px-4 py-3 text-[13.5px] font-sans text-[#1B2A3A]/80">{item.service}</td>
              <td className="px-4 py-3">
                <a
                  href={`tel:${item.number.replace(/\s/g, '')}`}
                  className="font-sans font-bold text-[#1B2A3A] hover:text-[#6B9FD4] transition-colors text-[14px]"
                >
                  {item.number}
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CountrySectionBlock({ section }: { section: CountrySection }) {
  return (
    <div id={section.id} className="scroll-mt-28">
      <h2 className="font-serif font-light text-[28px] md:text-[32px] text-[#1B2A3A] mb-2 leading-tight">
        <span className="mr-3">{section.flag}</span>{section.label}
      </h2>
      <div className="w-12 h-px bg-[#6B9FD4] mb-8" />

      {/* Urgences */}
      <h3 className="font-sans text-[11px] uppercase tracking-[0.12em] font-semibold text-[#1B2A3A]/40 mb-4">
        Urgences vitales
      </h3>
      <div className="mb-8">
        <UrgencyTable items={section.urgencies} />
      </div>

      {/* Écoute */}
      <h3 className="font-sans text-[11px] uppercase tracking-[0.12em] font-semibold text-[#1B2A3A]/40 mb-4">
        Lignes d'écoute et soutien émotionnel
      </h3>
      <div className="grid sm:grid-cols-2 gap-4 mb-8" role="list" aria-label={`Lignes d'écoute — ${section.label}`}>
        {section.support.map((r, i) => <ResourceCard key={i} resource={r} />)}
      </div>

      {/* Violences */}
      <h3 className="font-sans text-[11px] uppercase tracking-[0.12em] font-semibold text-[#1B2A3A]/40 mb-4">
        Violences, agressions et traumas
      </h3>
      {section.violenceNote && (
        <div className="flex gap-3 bg-[#6B9FD4]/10 border border-[#6B9FD4]/20 rounded-xl px-5 py-4 mb-5">
          <span className="text-[18px] shrink-0 mt-0.5">⚠️</span>
          <p className="text-[13.5px] font-sans text-[#1B2A3A]/80 leading-relaxed">{section.violenceNote}</p>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4" role="list" aria-label={`Ressources violences — ${section.label}`}>
        {section.violence.map((r, i) => <ResourceCard key={i} resource={r} />)}
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ServicesAide() {
  const [activeTab, setActiveTab] = useState('belgique');

  const scrollTo = (id: string) => {
    setActiveTab(id);
    const el = document.getElementById(id);
    if (el) {
      const offset = 96;
      const y = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <>
      <SEO
        title="Services d'aide · Numéros d'urgence et soutien | Amargi"
        description="Numéros d'urgence et lignes d'écoute pour la Belgique, la France, le Luxembourg et la Suisse. Soutien psychologique, violences, crise suicidaire."
      />
      <SiteNav />

      <div className="min-h-screen bg-[#F9F5F0]">

        {/* ── En-tête ── */}
        <header className="max-w-[800px] mx-auto px-6 md:px-8 pt-20 pb-10">
          <h1 className="font-serif font-light text-[40px] md:text-[52px] text-[#1B2A3A] leading-tight mb-6">
            Services d'aide
          </h1>
          <p className="font-sans text-[16px] text-[#1B2A3A]/70 leading-relaxed max-w-[600px]">
            Ce répertoire recense les principales lignes d'écoute et numéros d'urgence pour la Belgique, la France, le Luxembourg et la Suisse. Si vous traversez une période de détresse, ces ressources sont là pour vous.
          </p>

          {/* Disclaimer médical */}
          <div
            className="mt-6 px-5 py-4 rounded-xl font-sans text-[13.5px] text-[#1B2A3A]/75 leading-relaxed"
            style={{ backgroundColor: 'rgba(203,187,168,0.15)', border: '1px solid rgba(203,187,168,0.4)' }}
            role="note"
          >
            Cette page est une ressource d'orientation. Elle ne constitue pas un avis médical ou psychologique. En cas de danger immédiat, composez le{' '}
            <a href="tel:112" className="font-bold text-[#1B2A3A] hover:text-[#6B9FD4] transition-colors">112</a>{' '}
            (Europe).
          </div>
        </header>

        {/* ── Bloc urgence pan-européen ── */}
        <div className="max-w-[800px] mx-auto px-6 md:px-8 mb-12">
          <div
            className="rounded-2xl px-6 py-7 text-white"
            style={{ backgroundColor: '#2B5F8E' }}
            role="region"
            aria-label="Numéros d'urgence pan-européens"
          >
            <p className="font-sans text-[11px] uppercase tracking-[0.12em] font-semibold text-white/60 mb-4">
              Urgence — numéros européens
            </p>
            <div className="grid sm:grid-cols-3 gap-5">
              {[
                { label: 'Urgences Générales (Europe)', number: '112', note: '24h/24, 7j/7' },
                { label: 'Enfants Disparus (Europe)', number: '116 000', note: '24h/24, 7j/7' },
                { label: 'Aide aux Enfants (Europe)', number: '116 111', note: 'Variable selon pays' },
              ].map((item) => (
                <div key={item.number} className="flex flex-col gap-1">
                  <span className="text-[12px] text-white/70 font-sans">{item.label}</span>
                  <a
                    href={`tel:${item.number.replace(/\s/g, '')}`}
                    className="font-sans font-bold text-[28px] text-white hover:text-white/80 transition-colors leading-none"
                    aria-label={`Appeler le ${item.number}`}
                  >
                    {item.number}
                  </a>
                  <span className="text-[11px] text-white/50 font-sans">{item.note}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Navigation pays ── */}
        <div className="sticky top-0 z-20 bg-[#F9F5F0]/90 backdrop-blur-sm border-b border-[#CBBBA8]/30">
          <div className="max-w-[800px] mx-auto px-6 md:px-8">
            <nav className="flex gap-1 overflow-x-auto py-3 scrollbar-hide" aria-label="Navigation par pays">
              {COUNTRIES.map((c) => (
                <button
                  key={c.id}
                  onClick={() => scrollTo(c.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-sans font-medium whitespace-nowrap transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2B5F8E] ${
                    activeTab === c.id
                      ? 'bg-[#2B5F8E] text-white'
                      : 'text-[#1B2A3A]/60 hover:text-[#1B2A3A] hover:bg-[#CBBBA8]/20'
                  }`}
                  aria-current={activeTab === c.id ? 'true' : undefined}
                >
                  <span>{c.flag}</span>
                  <span>{c.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* ── Contenu par pays ── */}
        <main className="max-w-[800px] mx-auto px-6 md:px-8 py-12 space-y-16">
          {COUNTRIES.map((section) => (
            <CountrySectionBlock key={section.id} section={section} />
          ))}

          {/* ── Trouver un professionnel ── */}
          <section id="professionnels" className="scroll-mt-28">
            <h2 className="font-serif font-light text-[28px] md:text-[32px] text-[#1B2A3A] mb-2">
              Trouver un·e psychologue ou psychiatre
            </h2>
            <div className="w-12 h-px bg-[#6B9FD4] mb-6" />
            <p className="font-sans text-[15px] text-[#1B2A3A]/70 leading-relaxed mb-8">
              Pour un accompagnement thérapeutique au long cours, voici les annuaires officiels par pays.
            </p>
            <div className="overflow-x-auto rounded-xl border border-[#CBBBA8]/30">
              <table className="w-full min-w-[360px]" role="table" aria-label="Annuaires de professionnels par pays">
                <thead>
                  <tr className="bg-[#F9F5F0]">
                    <th className="text-left px-4 py-3 text-[11px] uppercase tracking-widest text-[#1B2A3A]/50 font-sans font-semibold">Pays</th>
                    <th className="text-left px-4 py-3 text-[11px] uppercase tracking-widest text-[#1B2A3A]/50 font-sans font-semibold">Ressource</th>
                    <th className="text-left px-4 py-3 text-[11px] uppercase tracking-widest text-[#1B2A3A]/50 font-sans font-semibold">Lien</th>
                  </tr>
                </thead>
                <tbody>
                  {PROFESSIONAL_RESOURCES.map((r, i) => (
                    <tr key={i} className="border-t border-[#CBBBA8]/20 bg-white">
                      <td className="px-4 py-3 text-[18px]">{r.country}</td>
                      <td className="px-4 py-3 text-[13.5px] font-sans text-[#1B2A3A]/80">{r.label}</td>
                      <td className="px-4 py-3">
                        <a
                          href={r.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[13px] text-[#2B5F8E] hover:underline font-sans"
                        >
                          {r.display}
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ── Disclaimer final ── */}
          <footer
            className="rounded-xl px-5 py-5 font-sans text-[13px] text-[#1B2A3A]/60 leading-relaxed"
            style={{ backgroundColor: 'rgba(203,187,168,0.12)', border: '1px solid rgba(203,187,168,0.3)' }}
          >
            <p>
              Ce répertoire est mis à jour régulièrement. En cas de danger immédiat, composez le{' '}
              <a href="tel:112" className="font-bold text-[#1B2A3A]/80 hover:text-[#6B9FD4] transition-colors">112</a>{' '}
              (numéro européen universel). Si vous êtes en Suisse, le{' '}
              <a href="tel:117" className="font-bold text-[#1B2A3A]/80 hover:text-[#6B9FD4] transition-colors">117</a>{' '}
              (police) ou le{' '}
              <a href="tel:144" className="font-bold text-[#1B2A3A]/80 hover:text-[#6B9FD4] transition-colors">144</a>{' '}
              (ambulance) sont les numéros d'urgence.
            </p>
            <p className="mt-2 text-[12px] text-[#1B2A3A]/40">Dernière mise à jour : juillet 2026</p>
          </footer>
        </main>
      </div>

      <BlogFooter />
    </>
  );
}
